<template>
  <div class="mat-panel" @click.stop>
    <div class="mat-header">
      <span class="mat-title">材质编辑</span>
      <button class="close-btn" @click="$emit('close')"><X :size="14" /></button>
    </div>

    <!-- 材质区域选择 -->
    <div v-if="meshNames.length > 1" class="mesh-list">
      <button
        v-for="name in meshNames"
        :key="name"
        :class="['mesh-chip', { active: selectedMesh === name }]"
        @click="selectedMesh = name"
        :title="name"
      >
        {{ name || '(无名)' }}
      </button>
      <button
        :class="['mesh-chip', { active: selectedMesh === '*' }]"
        @click="selectedMesh = '*'"
      >全部</button>
    </div>

    <!-- 内置材质库 -->
    <div class="section-label">快捷材质</div>
    <div class="preset-row">
      <button
        v-for="p in presets"
        :key="p.key"
        class="preset-btn"
        :style="{ background: p.color }"
        :title="p.label"
        @click="applyPreset(p)"
      >{{ p.label }}</button>
    </div>

    <!-- 颜色 -->
    <div class="section-label">颜色</div>
    <div class="color-row">
      <input type="color" v-model="current.color" class="color-picker" @input="onColorChange" />
      <input v-model="current.color" class="color-text" maxlength="7" @change="onColorChange" />
    </div>

    <!-- 材质类型 -->
    <div class="section-label">材质类型</div>
    <div class="type-row">
      <button
        v-for="t in matTypes"
        :key="t.key"
        :class="['type-btn', { active: current.type === t.key }]"
        @click="setType(t.key as any)"
      >{{ t.label }}</button>
    </div>

    <!-- 光泽度 -->
    <div class="section-label">光泽度 <span class="val">{{ Math.round(glossiness * 100) }}%</span></div>
    <input type="range" min="0" max="1" step="0.05"
      v-model.number="glossiness"
      @change="apply"
      class="slider"
    />

    <!-- 透明度 -->
    <div class="section-label">透明度 <span class="val">{{ Math.round((current.opacity ?? 1) * 100) }}%</span></div>
    <input type="range" min="0" max="1" step="0.05"
      v-model.number="current.opacity"
      @input="touched.opacity = true; previewMaterialOpacity(props.objectId, current.meshName || '*', current.opacity ?? 1)"
      @change="apply"
      class="slider"
    />

    <!-- 贴图 -->
    <div class="section-label">颜色贴图</div>
    <div class="texture-row">
      <div v-if="current.mapDataUrl" class="tex-preview" :style="{ backgroundImage: `url(${current.mapDataUrl})` }" />
      <button class="tex-btn" @click="uploadTexture('map')"><Upload :size="12" /> 上传</button>
      <button v-if="current.mapDataUrl" class="tex-btn danger" @click="clearTexture('map')"><X :size="12" /></button>
    </div>

    <div class="section-label">法线贴图</div>
    <div class="texture-row">
      <div v-if="current.normalMapDataUrl" class="tex-preview" :style="{ backgroundImage: `url(${current.normalMapDataUrl})` }" />
      <button class="tex-btn" @click="uploadTexture('normal')"><Upload :size="12" /> 上传</button>
      <button v-if="current.normalMapDataUrl" class="tex-btn danger" @click="clearTexture('normal')"><X :size="12" /></button>
    </div>

    <div class="section-label">粗糙度贴图</div>
    <div class="texture-row">
      <div v-if="current.roughnessMapDataUrl" class="tex-preview" :style="{ backgroundImage: `url(${current.roughnessMapDataUrl})` }" />
      <button class="tex-btn" @click="uploadTexture('roughness')"><Upload :size="12" /> 上传</button>
      <button v-if="current.roughnessMapDataUrl" class="tex-btn danger" @click="clearTexture('roughness')"><X :size="12" /></button>
    </div>

    <div class="mat-footer">
      <button class="btn-reset" @click="reset">恢复原始</button>
      <button class="btn-all" @click="applyToAll">应用到全部</button>
    </div>

    <!-- 隐藏的文件 input -->
    <input ref="texInputRef" type="file" accept="image/png,image/jpeg" style="display:none" @change="onTexFileChange" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { X, Upload } from 'lucide-vue-next'
import * as THREE from 'three'
import {
  getThreeObject,
  previewMaterialOpacity,
  previewMaterialRoughness,
  restoreImportMaterialsForMeshSelector,
} from '../three/ObjectManager'
import { useSceneStore } from '../stores/scene'
import type { MaterialOverride } from '../types/scene'

const props = defineProps<{
  objectId: string
  meshName?: string
  onHistoryPush?: () => void
}>()

const emit = defineEmits<{
  close: []
  changed: [overrides: MaterialOverride[]]
}>()

const sceneStore = useSceneStore()
const texInputRef = ref<HTMLInputElement | null>(null)
const selectedMesh = ref(props.meshName || '*')
let pendingTexType: 'map' | 'normal' | 'roughness' = 'map'

// 获取模型的所有 mesh 名称
const meshNames = computed(() => {
  const group = getThreeObject(props.objectId)
  if (!group) return []
  const names: string[] = []
  group.traverse(child => {
    if (child instanceof THREE.Mesh) names.push(child.name)
  })
  return [...new Set(names)]
})

// 当前编辑的材质数据
const current = ref<MaterialOverride>({
  meshName: selectedMesh.value,
  color: '#888888',
  type: 'matte',
  roughness: 0.7,
  metalness: 0,
  opacity: 1
})

// 仅当用户真正改过的字段才写入 store，避免用默认值覆盖 GLB 原有效果
const touched = ref({ roughness: false, metalness: false, opacity: false, color: false })

// 光泽度（0–1）与 roughness（1–0）双向绑定，拖动时实时预览
const glossiness = computed({
  get: () => 1 - (current.value.roughness ?? 0.7),
  set: (v: number) => {
    touched.value.roughness = true
    const r = 1 - v
    current.value.roughness = r
    previewMaterialRoughness(props.objectId, current.value.meshName || '*', r)
  }
})

// 从 store 加载已有覆盖
watch(() => props.objectId, loadExistingOverride, { immediate: true })
watch(selectedMesh, () => {
  current.value.meshName = selectedMesh.value
  loadExistingOverride()
})

function loadExistingOverride() {
  const obj = sceneStore.activeObjects().find(o => o.id === props.objectId)
  const override = obj?.materialOverrides?.find(m => m.meshName === selectedMesh.value)
  if (override) {
    current.value = { ...override }
    touched.value = {
      roughness: override.roughness !== undefined,
      metalness: override.metalness !== undefined,
      opacity: override.opacity !== undefined,
      color: !!override.color
    }
  } else {
    current.value = {
      meshName: selectedMesh.value,
      color: '#888888',
      type: 'matte',
      roughness: 0.7,
      metalness: 0,
      opacity: 1
    }
    touched.value = { roughness: false, metalness: false, opacity: false, color: false }
  }
}

const matTypes = [
  { key: 'matte', label: '哑光' },
  { key: 'metal', label: '金属' },
  { key: 'plastic', label: '塑料' },
  { key: 'glass', label: '玻璃' }
]

const presets = [
  { key: 'steel', label: '磨砂金属', color: '#607080', type: 'metal', roughness: 0.4, metalness: 0.85 },
  { key: 'plastic_white', label: '白塑料', color: '#e8e8e8', type: 'plastic', roughness: 0.5, metalness: 0 },
  { key: 'concrete', label: '混凝土', color: '#909090', type: 'matte', roughness: 0.95, metalness: 0 },
  { key: 'glass', label: '玻璃', color: '#a0c0e0', type: 'glass', roughness: 0.1, metalness: 0 },
  { key: 'rubber', label: '橡胶', color: '#303030', type: 'matte', roughness: 0.9, metalness: 0 },
  { key: 'paint', label: '烤漆', color: '#ffffff', type: 'plastic', roughness: 0.3, metalness: 0 },
  { key: 'yellow_floor', label: '防滑地', color: '#e8c020', type: 'matte', roughness: 0.8, metalness: 0 }
]

function applyPreset(p: typeof presets[0]) {
  touched.value.color = true
  touched.value.roughness = true
  touched.value.metalness = true
  current.value.color = p.color
  current.value.type = p.type as any
  current.value.roughness = p.roughness
  current.value.metalness = p.metalness
  apply()
}

function setType(type: 'matte' | 'metal' | 'plastic' | 'glass') {
  touched.value.roughness = true
  touched.value.metalness = true
  touched.value.opacity = true
  current.value.type = type
  switch (type) {
    case 'metal':
      current.value.roughness = 0.3
      current.value.metalness = 0.9
      current.value.opacity = 1
      break
    case 'plastic':
      current.value.roughness = 0.5
      current.value.metalness = 0
      current.value.opacity = 1
      break
    case 'glass':
      current.value.roughness = 0.1
      current.value.metalness = 0
      current.value.opacity = 0.3
      break
    case 'matte':
    default:
      current.value.roughness = 1.0
      current.value.metalness = 0
      current.value.opacity = 1
      break
  }
  apply()
}

function onColorChange() {
  touched.value.color = true
  apply()
}

function apply() {
  const obj = sceneStore.activeObjects().find(o => o.id === props.objectId)
  if (!obj) return
  props.onHistoryPush?.()
  const overrides = [...(obj.materialOverrides ?? [])]
  const idx = overrides.findIndex(o => o.meshName === current.value.meshName)
  const c = current.value
  const prev = idx >= 0 ? overrides[idx] : null
  const newOverride: MaterialOverride = {
    ...prev,
    meshName: c.meshName,
    type: c.type ?? 'matte'
  }
  if (touched.value.roughness && c.roughness !== undefined) newOverride.roughness = c.roughness
  if (touched.value.metalness && c.metalness !== undefined) newOverride.metalness = c.metalness
  if (touched.value.opacity && c.opacity !== undefined) newOverride.opacity = c.opacity
  if (touched.value.color && c.color) newOverride.color = c.color
  if (c.mapDataUrl) newOverride.mapDataUrl = c.mapDataUrl
  if (c.normalMapDataUrl) newOverride.normalMapDataUrl = c.normalMapDataUrl
  if (c.roughnessMapDataUrl) newOverride.roughnessMapDataUrl = c.roughnessMapDataUrl
  if (idx >= 0) overrides[idx] = newOverride
  else overrides.push(newOverride)
  sceneStore.updateObject(props.objectId, { materialOverrides: overrides })
  emit('changed', overrides)
}

function applyToAll() {
  const obj = sceneStore.activeObjects().find(o => o.id === props.objectId)
  if (!obj) return
  props.onHistoryPush?.()
  const names = meshNames.value.length > 0 ? meshNames.value : ['*']
  const overrides = names.map(name => ({ ...current.value, meshName: name }))
  sceneStore.updateObject(props.objectId, { materialOverrides: overrides })
  emit('changed', overrides)
}

function reset() {
  const obj = sceneStore.activeObjects().find(o => o.id === props.objectId)
  if (!obj) return
  props.onHistoryPush?.()
  const key = current.value.meshName || '*'
  const overrides = (obj.materialOverrides ?? []).filter(o => o.meshName !== key)
  sceneStore.updateObject(props.objectId, { materialOverrides: overrides })
  // 从 GPU 还原：此前 store 清空后 applyMaterialOverrides 会早退，且滑块预览可能直接改了原材质
  restoreImportMaterialsForMeshSelector(props.objectId, key)
  loadExistingOverride()
  emit('changed', overrides)
}

function uploadTexture(type: 'map' | 'normal' | 'roughness') {
  pendingTexType = type
  texInputRef.value?.click()
}

function onTexFileChange(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = () => {
    const dataUrl = reader.result as string
    if (pendingTexType === 'map') current.value.mapDataUrl = dataUrl
    else if (pendingTexType === 'normal') current.value.normalMapDataUrl = dataUrl
    else if (pendingTexType === 'roughness') current.value.roughnessMapDataUrl = dataUrl
    apply()
  }
  reader.readAsDataURL(file)
  ;(e.target as HTMLInputElement).value = ''
}

function clearTexture(type: 'map' | 'normal' | 'roughness') {
  if (type === 'map') current.value.mapDataUrl = undefined
  else if (type === 'normal') current.value.normalMapDataUrl = undefined
  else if (type === 'roughness') current.value.roughnessMapDataUrl = undefined
  apply()
}
</script>

<style scoped>
.mat-panel {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 10px;
  width: 240px;
  padding: 0;
  user-select: none;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
}

.mat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 8px;
  border-bottom: 1px solid #334155;
}

.mat-title { font-size: 13px; font-weight: 600; color: #e2e8f0; }

.close-btn {
  background: none; border: none; cursor: pointer;
  color: #64748b; padding: 2px; border-radius: 3px;
  display: flex; align-items: center;
}
.close-btn:hover { color: #e2e8f0; background: #334155; }

.mesh-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 10px 0;
}

.mesh-chip {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid #334155;
  background: #0f172a;
  color: #64748b;
  cursor: pointer;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mesh-chip.active { border-color: #3b82f6; color: #60a5fa; background: #1e3a5f; }

.section-label {
  font-size: 10px;
  color: #64748b;
  padding: 6px 12px 2px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.val { color: #94a3b8; font-weight: 500; text-transform: none; }

.preset-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 10px;
}

.preset-btn {
  font-size: 10px;
  padding: 3px 7px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.85);
  cursor: pointer;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
}
.preset-btn:hover { filter: brightness(1.15); }

.color-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
}

.color-picker {
  width: 32px;
  height: 28px;
  border: 1px solid #334155;
  border-radius: 5px;
  padding: 2px;
  background: none;
  cursor: pointer;
}

.color-text {
  flex: 1;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 5px;
  color: #e2e8f0;
  padding: 4px 8px;
  font-size: 12px;
  font-family: monospace;
  outline: none;
}
.color-text:focus { border-color: #3b82f6; }

.type-row {
  display: flex;
  gap: 4px;
  padding: 4px 10px;
}

.type-btn {
  flex: 1;
  font-size: 11px;
  padding: 4px 0;
  border-radius: 5px;
  border: 1px solid #334155;
  background: #0f172a;
  color: #64748b;
  cursor: pointer;
}
.type-btn.active { border-color: #3b82f6; color: #60a5fa; background: #1e3a5f; }
.type-btn:hover:not(.active) { border-color: #475569; color: #94a3b8; }

.slider {
  width: calc(100% - 24px);
  margin: 0 12px 4px;
  accent-color: #3b82f6;
  display: block;
}

.texture-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
}

.tex-preview {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid #334155;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
}

.tex-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #334155;
  background: #0f172a;
  color: #64748b;
  cursor: pointer;
}
.tex-btn:hover { border-color: #475569; color: #e2e8f0; }
.tex-btn.danger:hover { border-color: #ef4444; color: #f87171; }

.mat-footer {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  padding: 8px 10px;
  border-top: 1px solid #334155;
  margin-top: 6px;
}

.btn-reset, .btn-all {
  flex: 1;
  font-size: 11px;
  padding: 5px 0;
  border-radius: 5px;
  cursor: pointer;
}

.btn-reset {
  background: #0f172a;
  border: 1px solid #334155;
  color: #64748b;
}
.btn-reset:hover { border-color: #475569; color: #94a3b8; }

.btn-all {
  background: #1e3a5f;
  border: 1px solid #3b82f6;
  color: #60a5fa;
}
.btn-all:hover { background: #1e4080; }
</style>
